import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { naMarketingoveStrance } from "@/lib/mereni";

const STORAGE_KEY = "bp_cookie_consent_v1";

const CB_TX = {
  cs: { title: "Soukromí na prvním místě", body: "Používáme jen technické cookies nezbytné k fungování aplikace (přihlášení). Žádné reklamní ani sledovací cookies třetích stran.", link: "Zásady ochrany os. údajů", close: "Zavřít", accept: "Rozumím" },
  sk: { title: "Súkromie na prvom mieste", body: "Používame iba technické cookies nevyhnutné pre fungovanie aplikácie (prihlásenie). Žiadne reklamné ani sledovacie cookies tretích strán.", link: "Zásady ochrany osobných údajov", close: "Zavrieť", accept: "Rozumiem" },
  en: { title: "Privacy first", body: "We only use technical cookies essential for the app to work (login). No advertising or third-party tracking.", link: "Privacy policy", close: "Close", accept: "Got it" },
  uk: { title: "Конфіденційність на першому місці", body: "Ми використовуємо лише технічні файли cookie, необхідні для роботи додатка (вхід). Жодних рекламних або сторонніх трекерів.", link: "Політика конфіденційності", close: "Закрити", accept: "Зрозуміло" },
  vi: { title: "Quyền riêng tư là trên hết", body: "Chúng tôi chỉ dùng cookie kỹ thuật cần thiết cho ứng dụng (đăng nhập). Không có quảng cáo hay theo dõi từ bên thứ ba.", link: "Chính sách bảo mật", close: "Đóng", accept: "Đã hiểu" },
};

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const { lang } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const onPrivacy = location.pathname === "/privacy"; // na /privacy neodkazuj sám na sebe (#8)
  /* Veřejné marketingové stránky poradce (/w, /l, /t, /kviz) mají vlastní lištu
   * components/web/MericiKody.js (technické cookies + souhlas s měřením) — tady
   * ustoupíme, ať nejsou dvě lišty přes sebe (25. 10. 2026). */
  const naMarketingove = naMarketingoveStrance(location.pathname);
  const C = CB_TX[lang] || CB_TX.cs;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (naMarketingove) { setOpen(false); return; }
    // Logged-in users have implicitly accepted technical cookies (the session
    // cookie itself is technical) — auto-record consent and hide the banner.
    // POZOR na `setOpen(false)`: lišta se otevírá se zpožděním 1,2 s, takže se
    // stihla ukázat ještě před přihlášením a po něm už ji nic nezavřelo —
    // visela přes obsah nástěnky, dokud ji poradce ručně neodklikl.
    if (user) {
      if (!localStorage.getItem(STORAGE_KEY)) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ level: "essential", ts: Date.now(), auto: true }));
      }
      setOpen(false);
      return;
    }
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user, naMarketingove]);

  const accept = (level) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ level, ts: Date.now() }));
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: "spring", damping: 24 }}
          className="fixed bottom-0 left-0 right-0 z-[150] px-3 pb-3 pointer-events-none"
          data-testid="cookie-banner"
        >
          {/* Slim full-width lišta dole — nepřekrývá centrovanou kartu (login/registrace/reset) */}
          <div className="mx-auto max-w-3xl flex items-center gap-3 rounded-xl px-4 py-2.5 backdrop-blur-xl pointer-events-auto"
            style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-float, 0 12px 40px rgba(0,0,0,0.18))" }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)" }}>
              <Cookie size={15} style={{ color: "var(--accent)" }} />
            </div>
            <p className="flex-1 min-w-0 text-[12px] leading-snug" style={{ color: "var(--muted)" }}>
              <span className="font-semibold" style={{ color: "var(--ink)" }}>{C.title}.</span>{" "}
              <span className="hidden sm:inline">{C.body}{" "}</span>
              {!onPrivacy && (
                <Link to="/privacy" className="underline whitespace-nowrap" style={{ color: "var(--accent)" }}>{C.link}</Link>
              )}
            </p>
            <button
              onClick={() => accept("essential")}
              className="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
              style={{ background: "var(--accent)", color: "#fff" }}
              data-testid="cookie-accept"
            >
              {C.accept}
            </button>
            <button
              onClick={() => accept("essential")}
              className="flex-shrink-0 p-1 opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: "var(--muted)" }}
              aria-label={C.close}
              data-testid="cookie-close"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
